/**
 * F-148 §3.2 save status chip: saved / saving / unsaved / failed / version conflict.
 * Colour is never the only signal: each state has an icon and translated text, and the chip is
 * a polite live region so screen readers hear transitions.
 */
import { AlertTriangle, Check, CloudOff, Loader2, RefreshCw } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { SaveStatus } from '../lib/draft-reducer';
import { receivingErrorKey, receivingErrorOptions } from '../lib/error-messages';

export interface SaveStatusChipProps {
  readonly status: SaveStatus;
  readonly error?: unknown;
  /** True when local validation blocks the payload from being sent (§3.2 "fields with parse errors are not sent"). */
  readonly blockedByFields?: boolean;
  readonly onRetry?: () => void;
  readonly onReload?: () => void;
  /**
   * False for a second, purely visual instance of the same status (e.g. a sticky bar) so screen
   * readers hear each transition once. Defaults to a polite live region.
   */
  readonly live?: boolean;
  readonly className?: string;
}

export function SaveStatusChip({
  status,
  error,
  blockedByFields = false,
  onRetry,
  onReload,
  live = true,
  className,
}: SaveStatusChipProps): React.JSX.Element {
  const { t } = useTranslation();

  const tone =
    status === 'saved'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-200'
      : status === 'saving'
        ? 'border-input bg-admin-surface-muted text-muted-foreground'
        : status === 'unsaved'
          ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-200'
          : 'border-destructive/40 bg-destructive/10 text-destructive';

  const label =
    status === 'saved'
      ? t('admin.receiving.save.saved')
      : status === 'saving'
        ? t('admin.receiving.save.saving')
        : status === 'unsaved'
          ? blockedByFields
            ? t('admin.receiving.save.fixFields')
            : t('admin.receiving.save.unsaved')
          : status === 'conflict'
            ? t('admin.receiving.draft.versionConflict')
            : t('admin.receiving.save.failed');

  const Icon =
    status === 'saved'
      ? Check
      : status === 'saving'
        ? Loader2
        : status === 'unsaved'
          ? CloudOff
          : AlertTriangle;

  const detail =
    status === 'failed' && error !== undefined && error !== null
      ? t(receivingErrorKey(error), receivingErrorOptions(error))
      : null;

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2 text-start', className)}
      data-testid="save-status-chip"
      data-status={status}
    >
      <span
        role={live ? 'status' : undefined}
        aria-live={live ? 'polite' : 'off'}
        aria-atomic={live ? 'true' : undefined}
        className={cn(
          'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium',
          tone,
        )}
      >
        <Icon
          aria-hidden="true"
          className={cn(
            'size-3.5',
            status === 'saving' ? 'animate-spin motion-reduce:animate-none' : '',
          )}
        />
        <span>{label}</span>
        {detail ? <span className="sr-only">{detail}</span> : null}
      </span>
      {detail ? (
        <span className="text-destructive text-xs" aria-hidden="true">
          {detail}
        </span>
      ) : null}
      {status === 'failed' && onRetry ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11"
          onClick={onRetry}
          data-testid="save-retry"
        >
          <RefreshCw aria-hidden="true" className="size-3.5" />
          {t('admin.receiving.save.retry')}
        </Button>
      ) : null}
      {status === 'conflict' && onReload ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11"
          onClick={onReload}
          data-testid="save-reload"
        >
          <RefreshCw aria-hidden="true" className="size-3.5" />
          {t('admin.receiving.draft.reload')}
        </Button>
      ) : null}
    </div>
  );
}
